import type { ServiceDisplayDayPart } from '../day-part/utils';
import type { ServiceDisplayLayoutSnapshot } from './types';

/**
 * Collect all unique day part IDs referenced in a layout's widget thresholds.
 */
export function getUniqueDayPartIdsFromLayout(
  layout: ServiceDisplayLayoutSnapshot | undefined,
): Set<string> {
  if (!layout?.widgets?.length) return new Set();
  const ids = new Set<string>();
  for (const widget of layout.widgets) {
    for (const t of widget.thresholds ?? []) {
      if (t.dayPartsId) ids.add(t.dayPartsId);
    }
  }
  return ids;
}

/**
 * Filter day parts to only those referenced by the layout, preserving order of allDayParts.
 */
export function getDayPartsForLayout(
  layout: ServiceDisplayLayoutSnapshot | undefined,
  allDayParts: ServiceDisplayDayPart[],
): ServiceDisplayDayPart[] {
  const idSet = getUniqueDayPartIdsFromLayout(layout);
  if (idSet.size === 0) return [];
  return allDayParts.filter((dp) => idSet.has(dp.id));
}

/**
 * Collect all unique day part IDs referenced in any widget threshold across all layouts.
 */
export function getAssignedDayPartIdsFromAllLayouts(
  layouts: ReadonlyArray<ServiceDisplayLayoutSnapshot>,
): Set<string> {
  const ids = new Set<string>();
  for (const layout of layouts) {
    if (!layout?.widgets?.length) continue;
    for (const widget of layout.widgets) {
      for (const t of widget.thresholds ?? []) {
        if (t.dayPartsId) ids.add(t.dayPartsId);
      }
    }
  }
  return ids;
}

/**
 * Map each day part ID to the list of layout names that have a widget threshold using it.
 * Used to show "conflicts with … in the following layout(s): Layout A, Layout B" in the day part dialog.
 */
export function getDayPartIdToLayoutNames(
  layouts: ReadonlyArray<ServiceDisplayLayoutSnapshot>,
): Map<string, string[]> {
  const map = new Map<string, string[]>();
  for (const layout of layouts) {
    if (!layout?.widgets?.length) continue;
    const layoutName = layout.name ?? '';
    for (const widget of layout.widgets) {
      for (const t of widget.thresholds ?? []) {
        if (!t.dayPartsId) continue;
        const list = map.get(t.dayPartsId) ?? [];
        if (!list.includes(layoutName)) list.push(layoutName);
        map.set(t.dayPartsId, list);
      }
    }
  }
  return map;
}
